/**
 * Leads API — contact form submissions.
 *
 * Enquiries were stored and emailed, then never read back. A row recording a
 * failed delivery was written faithfully and seen by nobody, so an enquiry that
 * was safe in the database was in practice lost. These are the reads that make
 * storing-before-sending actually pay off.
 */

import { apiGet, apiPost } from './client';

export type DeliveryStatus = 'pending' | 'sent' | 'failed';

export interface Submission {
  id: string;
  website_id: string;
  name: string;
  email: string;
  phone: string | null;
  message: string;
  delivered_to: string | null;
  delivery_status: DeliveryStatus;
  delivery_error: string | null;
  created_at: string;
  /** Present only on the cross-site failed list. */
  business_name?: string | null;
}

export interface SubmissionsResponse {
  success: boolean;
  data: {
    websiteId: string;
    businessName: string;
    submissions: Submission[];
    total: number;
    failed: number;
    pending: number;
    limit: number;
    offset: number;
  };
}

export interface FailedSubmissionsResponse {
  success: boolean;
  data: {
    submissions: Submission[];
    count: number;
  };
}

export interface ResendResponse {
  success: boolean;
  error?: string;
  data: {
    submissionId?: string;
    deliveryStatus: DeliveryStatus;
    deliveredTo?: string[];
    error?: string | null;
  };
}

export function getSubmissions(websiteId: string, limit = 50, offset = 0): Promise<SubmissionsResponse> {
  return apiGet<SubmissionsResponse>(
    `/api/admin/sites/${websiteId}/submissions?limit=${limit}&offset=${offset}`
  );
}

/** Undelivered enquiries across every site — drives the dashboard alert. */
export function getFailedSubmissions(): Promise<FailedSubmissionsResponse> {
  return apiGet<FailedSubmissionsResponse>('/api/admin/submissions/failed');
}

/**
 * Re-send one enquiry. This genuinely re-sends through the same delivery path
 * as the original, and the status returned is the real outcome — it is not
 * marked sent optimistically.
 */
export function resendSubmission(submissionId: string): Promise<ResendResponse> {
  return apiPost<ResendResponse>(`/api/admin/submissions/${submissionId}/resend`);
}
